import React, { useState, useEffect } from 'react';
import { estadoDelPrestamo } from '../utils/prestamo';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const moneda = (n) => '$' + Math.round(n || 0).toLocaleString('es-CO');

function Dashboard({ dashboard, clients, onRefresh }) {
  // El mejor cliente y el de atención vienen ya resueltos del servidor. Antes
  // se sacaban pidiendo el puntaje uno por uno: 114 peticiones en la primera
  // pantalla que se abre.
  const topClient = dashboard.mejor || null;
  const riskClient = dashboard.atencion || null;

  // Las cuentas se hacen aquí con estadoDelPrestamo, la misma función que usan
  // Pagos, Deuda y Morosos. Cuando las hacía el servidor con su propia versión,
  // las dos se separaron: contaba meses de 30 días frente a meses de calendario,
  // y sumaba los abonos a capital como si fueran ganancia.
  const cuentas = (dashboard.prestamos || []).reduce((acc, p) => {
    const e = estadoDelPrestamo(p, p.pagos || []);
    return {
      generado: acc.generado + e.interesGenerado,
      // Solo el interés cobrado es ganancia. El capital que devuelven es la
      // propia plata volviendo.
      cobrado: acc.cobrado + e.interesPagado,
      porCobrar: acc.porCobrar + Math.max(0, e.interesPendiente),
      proximoCobro: acc.proximoCobro + e.interesPorCobro
    };
  }, { generado: 0, cobrado: 0, porCobrar: 0, proximoCobro: 0 });

  const data = [
    { name: 'Total Prestado', value: dashboard.total_loaned || 0 },
    { name: 'Interés cobrado', value: cuentas.cobrado }
  ];

  const COLORS = ['#0088FE', '#00C49F'];

  return (
    <div className="dashboard">
      <h2>🏠 Inicio</h2>
      <button onClick={onRefresh} className="refresh-btn">🔄 Actualizar</button>

      {/* Cards de Totales */}
      <div className="cards-container">
        <div className="card">
          <h3>💵 Total Prestado</h3>
          <p className="amount">${((dashboard.total_loaned || 0) / 1000000).toFixed(1)}M</p>
          <small>${(dashboard.total_loaned || 0).toLocaleString('es-CO', { maximumFractionDigits: 0 })}</small>
        </div>
        {/* Interés cobrado y por cobrar, separados. Antes había un solo
            «Total Ganado» que mezclaba las dos cosas y además contaba como
            ganancia el capital que los clientes devolvían. */}
        <div className="card earning-card">
          <h3>💰 Interés cobrado</h3>
          <p className="amount earning">{moneda(cuentas.cobrado)}</p>
          <small>de {moneda(cuentas.generado)} generados</small>
        </div>
        <div className="card highlight">
          <h3>⏳ Interés por cobrar</h3>
          <p className="amount" style={{ color: cuentas.porCobrar > 0 ? '#b45309' : '#10b981' }}>
            {moneda(cuentas.porCobrar)}
          </p>
          <small>ya generado y sin pagar</small>
        </div>
        <div className="card">
          <h3>📈 Próximo cobro</h3>
          <p className="amount earning">{moneda(cuentas.proximoCobro)}</p>
          <small>si todos pagan su periodo</small>
        </div>
        <div className="card">
          <h3>📊 Total Préstamos</h3>
          <p className="amount">{dashboard.total_loans || 0}</p>
          <small>Activos</small>
        </div>
        {topClient && (
          <div className="card highlight" style={{ borderLeft: '4px solid #10b981' }}>
            <h3>👤 Mejor Calificado</h3>
            <p className="amount" style={{ color: '#10b981' }}>{'⭐'.repeat(Math.max(1, Math.round((topClient.credit_score || 0) / 20)))}</p>
            <small><strong>{topClient.name}</strong></small>
            <small>Puntaje: {topClient.credit_score || 0}/100</small>
          </div>
        )}
        {riskClient && (
          <div className="card" style={{ borderLeft: '4px solid #ef4444' }}>
            <h3>📉 Necesita Atención</h3>
            <p className="amount" style={{ color: '#ef4444' }}>⚠️</p>
            <small><strong>{riskClient.name}</strong></small>
            <small>Puntaje: {riskClient.credit_score || 0}/100</small>
          </div>
        )}
      </div>

      {/* Gráficos */}
      <div className="charts-container">
        <div className="chart">
          <h3>Comparativa Prestado vs Ganado</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#8884d8" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="chart">
          <h3>Proporción Ganancia</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => `${name}: $${value}`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="info-box">
        <h3>📝 Resumen</h3>
        <p>
          Tienes <strong>{dashboard.total_loans || 0}</strong> préstamos activos.
          Has ganado <strong>${(dashboard.total_earned || 0).toFixed(2)}</strong> en intereses.
        </p>
      </div>
    </div>
  );
}

export default Dashboard;
